import Dump from 'components/Dump';
import Downshift from 'downshift';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import VirtualList from 'react-tiny-virtual-list-dmk';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { securitiesSelectors, securitiesUtils } from 'app-components/flux/securities';
import { transactionsUtils } from 'app-components/flux/transactions';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { investmentSymbolsActions, investmentSymbolsSelectors } from 'app-components/flux/investment-symbols';
import ErrorBoundary from 'components/ErrorBoundary';
import HighlightText from 'components/HighlightText';
import TickerAvatar from 'components/TickerAvatar';

const itemHeight = 48; //  The height of the items and the max # of items to be shown in the list
const itemsToShow = 7;

const useStyles = makeStyles()((theme) => ({
  popperStyle: {
    zIndex: theme.zIndex.modal,
  },
  noCompanyText: {
    fontWeight: 500,
  },
  itemList: {
    width: '96%',
    //  marginRight: '10px',
    marginLeft: '10px',
    height: itemHeight,
    borderRadius: '12px',
    '&.hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  itemListCreate: {
    borderTop: `1px solid ${theme.palette.border.main}`,
    minWidth: '500px',
    height: 60,
    borderRadius: '0px 0px 12px 12px',
    '&.hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  itemListTextCreate: {
    minWidth: '500px',
  },
  addBtnIcon: {
    color: theme.palette.primary.main,
  },
}));

function SymbolLookupField(props) {
  const { id, value, onSelected, onInputValueChange, hideCreateOption, inlineEdit, ...otherProps } = props;
  const dispatch = useDispatch();

  const { classes } = useStyles();

  const [filter, setFilter] = useState();

  /*  Shrink Label is used because there is an inexplicable MUI Bug that sometimes causes the label to not get shrunk when text is inside the field, despite text being present
   * and inputValue being undefined, text is there and it overlaps with the label when the label is unshrunk. Therefore, the label will remain shrunk as soon as the user inputs text.
   * This is a rather hacky solution, but a better alternative to overlapping text.  */
  const [shrinkLabel, setShrinkLabel] = useState(false);

  const stockLogo = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'stockLogo'));

  useEffect(() => {
    if (filter && filter !== value) {
      dispatch(
        investmentSymbolsActions.getInvestmentSymbols(undefined, {
          axiosConfig: { params: { keyword: filter } },
        }),
      );
    }
  }, [filter, value, dispatch]);

  //  ============= On First Render ============
  useEffect(() => {
    if (value) {
      setShrinkLabel(true);
    }
  }, []); /* eslint-disable-line react-hooks/exhaustive-deps */

  const searchKeys = useMemo(() => filter?.split?.(' '), [filter]);
  const symbolFilter = useMemo(() => searchKeys?.[0], [searchKeys]);
  const items = useSelector((state) => investmentSymbolsSelectors.getInvestmentSymbolsFiltered(state, symbolFilter));
  const investmentSymbolsIsLoading = useSelector((state) => investmentSymbolsSelectors.getIsLoading(state));
  const securitiesEnabled = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'securities'));
  const anchorElRef = useRef();
  const securityList = useSelector(securitiesSelectors.getResourcesById);
  const isDuplicateName = securitiesEnabled && securityList.some((security) => security.name === filter);

  return (
    <ErrorBoundary>
      <Downshift
        defaultHighlightedIndex={0}
        initialInputValue={value}
        itemToString={(item) => transactionsUtils.security({ symbol: item?.symbol, securityName: item?.name })}
        onChange={(item) => {
          if (item) {
            if (securitiesEnabled && !item.security?.id && item.name && inlineEdit) {
              securitiesUtils.createNewSecurityAndSendPackage(item, dispatch, assert, onSelected);
            } else {
              onSelected(securitiesUtils.createNewSecurityPackage(item));
            }
          }
        }}
        onInputValueChange={(inputValue) => onInputValueChange?.(inputValue)}
        id={id}
        stateReducer={(state, changes) => {
          let changesApproved = changes;
          // console.log('symbol lookup stateReducer: ', state, changes);
          switch (changes.type) {
            case Downshift.stateChangeTypes.blurInput:
              if (
                state.inputValue &&
                state.inputValue !== value &&
                !state.selectedItem?.symbol &&
                !state.selectedItem?.name &&
                !changes.selectedItem?.symbol &&
                !changes.selectedItem?.name
              ) {
                // clear input if nothing selected
                changesApproved = {
                  ...changes,
                  inputValue: '',
                };
              } else if (
                (!state.inputValue || changes.inputValue === '') &&
                !changes.selectedItem?.symbol &&
                !changes.selectedItem?.name
              ) {
                // clear selection if input is empty
                changesApproved = {
                  ...changes,
                  selectedItem: { symbol: '', name: '' },
                };
              }
              break;
            case Downshift.stateChangeTypes.changeInput: // clear selection if user typing
              if (state.selectedItem?.symbol || state.selectedItem?.name) {
                changesApproved = {
                  ...changes,
                  selectedItem: { symbol: '', name: '' },
                };
              }
              break;
            // case Downshift.stateChangeTypes.keyDownEscape:
            // case Downshift.stateChangeTypes.mouseUp:
            //   changesApproved = {
            //     ...changes,
            //     inputValue: undefined,
            //     selectedItem: undefined,
            //   };
            //   break;
            default:
              changesApproved = changes;
          }
          // console.log('symbol lookup changesApproved: ', changesApproved);
          return changesApproved;
        }}
      >
        {({
          getRootProps,
          getInputProps,
          getItemProps,
          getMenuProps,
          isOpen,
          inputValue,
          highlightedIndex,
          // selectHighlightedItem,
        }) => {
          setFilter(inputValue);
          return (
            <div {...getRootProps({}, { suppressRefError: true })}>
              <TextField
                {...otherProps}
                sharedcomponentid='SymbolLookupField'
                inputRef={anchorElRef}
                InputLabelProps={{ shrink: shrinkLabel }}
                onFocus={() => {
                  setShrinkLabel(true);
                }}
                InputProps={{
                  ...getInputProps({
                    autoComplete: 'off',
                    // onKeyDown: (event) => {
                    //   if (isOpen && event.key === 'Tab') {
                    //     selectHighlightedItem();
                    //   }
                    // },
                    ...otherProps.InputProps,
                  }),
                }}
              />

              <Popper
                open={isOpen}
                anchorEl={anchorElRef.current}
                placement='bottom-start'
                className={classes.popperStyle}
                //  style={{ zIndex: theme.zIndex.modal }}
              >
                <div {...getMenuProps({}, { suppressRefError: true })}>
                  <Paper elevation={12}>
                    {items.size > 0 && !investmentSymbolsIsLoading && (
                      <VirtualList
                        width='500px'
                        height={itemHeight * (items.size < itemsToShow ? items.size : itemsToShow + 0.5)}
                        itemSize={items.size > itemHeight ? itemHeight : items.size}
                        itemCount={items.size > itemHeight ? itemHeight : items.size}
                        overscanCount={40}
                        scrollToIndex={highlightedIndex}
                        scrollToAlignment='auto'
                        renderItem={({ index /* , style */ }) => {
                          const item = items.get(index);
                          if (!item) return null;
                          return (
                            <ListItem
                              button
                              className={classes.itemList}
                              {...getItemProps({
                                // style,
                                index,
                                key: item.name,
                                id: item.name,
                                item,
                                selected: index === highlightedIndex,
                              })}
                            >
                              <Dump obj={item} />
                              {stockLogo && (
                                <ListItemIcon>
                                  <TickerAvatar value={item.symbol} />
                                </ListItemIcon>
                              )}
                              <ListItemText
                                primary={<HighlightText text={item.name} searchKeys={searchKeys} />}
                                secondary={<HighlightText text={item.symbol} searchKeys={searchKeys} />}
                              />
                            </ListItem>
                          );
                        }}
                      />
                    )}
                    {investmentSymbolsIsLoading && (
                      <Box
                        display='flex'
                        alignSelf='center'
                        justifyContent='center'
                        alignItems='center'
                        marginTop='auto'
                        marginBottom='auto'
                        minHeight={234}
                        width='500px'
                      >
                        <CircularProgress size={70} />
                      </Box>
                    )}
                    {items.size <= 0 && !investmentSymbolsIsLoading && filter && (
                      <Box
                        display='flex'
                        alignSelf='center'
                        justifyContent='center'
                        alignItems='center'
                        marginTop='auto'
                        marginBottom='auto'
                        minHeight={234}
                        width='500px'
                      >
                        <Typography
                          variant='titleMedium'
                          className={classes.noCompanyText}
                        >{`No company matching "${filter}"`}</Typography>
                      </Box>
                    )}
                    {inputValue && !hideCreateOption && !isDuplicateName && (
                      <ListItem
                        button
                        className={classes.itemListCreate}
                        {...getItemProps({
                          index: items.size,
                          key: inputValue,
                          id: inputValue,
                          item: { name: inputValue }, // create custom
                          selected: items.size === highlightedIndex,
                        })}
                      >
                        <ListItemText
                          primary={
                            <HighlightText
                              className={classes.itemListTextCreate}
                              text={`Add security with "${inputValue}" name.`}
                              searchKeys={[`"${filter}"`]}
                            />
                          }
                        />
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <AddCircleIcon className={classes.addBtnIcon} />
                      </ListItem>
                    )}
                  </Paper>
                </div>
              </Popper>
            </div>
          );
        }}
      </Downshift>
    </ErrorBoundary>
  );
}

SymbolLookupField.propTypes = {
  inlineEdit: PropTypes.bool,
  onSelected: PropTypes.func,
  onInputValueChange: PropTypes.func,
  id: PropTypes.any,
  value: PropTypes.string,
  hideCreateOption: PropTypes.bool,

  // ferry props
  label: PropTypes.string,
  variant: PropTypes.string,
  fullWidth: PropTypes.boolean,
  error: PropTypes.string,
  helperText: PropTypes.string,
  InputProps: PropTypes.any,
};

export default SymbolLookupField;
